/**
 * Arabic messages (document 15): Gulf Modern Standard Arabic, FTA terminology. A draft for the
 * Arabic-speaking accountant's review (section 10); nothing here ships to a client until that
 * review. Plurals have all six CLDR forms; the test suite checks every English key has its Arabic.
 */
import { areasAr } from './areas/index.js';

export const ar = {
  ...areasAr,
  common: {
    save: 'حفظ',
    saving: 'جارٍ الحفظ…',
    saveAndClose: 'حفظ وإغلاق',
    saveAndNew: 'حفظ وجديد',
    cancel: 'إلغاء',
    close: 'إغلاق',
    new: 'جديد',
    edit: 'تعديل',
    apply: 'تطبيق',
    import: 'استيراد',
    runReport: 'عرض التقرير',
    viewRegister: 'عرض السجل',
    loading: 'جارٍ التحميل…',
    none: 'لا يوجد',
    yes: 'نعم',
    no: 'لا',
    on: 'مفعّل',
    off: 'غير مفعّل',
    inactive: 'غير نشط',
    optional: 'اختياري',
  },
  language: {
    en: 'English',
    ar: 'العربية',
    both: 'الإنجليزية والعربية',
  },
  shell: {
    client: 'الشركة:',
    selectClient: 'اختر شركة',
    switchClient: 'تبديل الشركة',
    findClient: 'ابحث عن شركة',
    signInDisabled: 'تسجيل الدخول غير مفعّل (لا يوجد VITE_OIDC_ISSUER).',
    noClients: 'لا توجد لديك شركات بعد. اطلب من المسؤول إضافتك إلى إحداها.',
    viewAllClients: 'عرض جميع الشركات',
    accountantTools: 'أدوات المحاسب',
    help: 'المساعدة',
    keyboardShortcuts: 'اختصارات لوحة المفاتيح',
    settings: 'الإعدادات',
    signOut: 'تسجيل الخروج',
    unread_zero: 'لا توجد رسائل غير مقروءة',
    unread_one: 'رسالة واحدة غير مقروءة',
    unread_two: 'رسالتان غير مقروءتين',
    unread_few: '{{count}} رسائل غير مقروءة',
    unread_many: '{{count}} رسالة غير مقروءة',
    unread_other: '{{count}} رسالة غير مقروءة',
    selectClientTitle: 'اختر شركة',
    selectClientHint: 'استخدم مبدّل الشركات في الشريط العلوي لاختيار الدفاتر التي تريد فتحها.',
  },
  roles: {
    outss_admin: 'مسؤول Outss',
    outss_accountant: 'محاسب',
    client_owner: 'المالك',
    client_viewer: 'مشاهد',
  },
  arabicField: {
    name: 'الاسم بالعربية',
    description: 'الوصف بالعربية',
    hintName: 'اختياري. يظهر لقرّاء العربية، ويُستخدم الاسم الإنجليزي إذا تُرك فارغاً.',
    hintDescription: 'اختياري. يظهر لقرّاء العربية، ويُستخدم الوصف الإنجليزي إذا تُرك فارغاً.',
    noArabic: 'لا توجد حروف عربية: سيرى قرّاء العربية النص كما كُتب تماماً.',
  },
  accounts: {
    title: 'دليل الحسابات',
    suggestArabic: 'اقتراح الأسماء العربية',
    filterPlaceholder: 'تصفية حسب الاسم أو الرقم',
    filterLabel: 'تصفية الحسابات',
    includeInactive: 'تضمين غير النشطة',
    missingArabic: 'بدون اسم عربي',
    count_zero: 'لا توجد حسابات',
    count_one: 'حساب واحد',
    count_two: 'حسابان',
    count_few: '{{count}} حسابات',
    count_many: '{{count}} حساباً',
    count_other: '{{count}} حساب',
    loadError: 'تعذّر تحميل الحسابات',
    noMatch: 'لا توجد حسابات مطابقة للتصفية',
    number: 'الرقم',
    name: 'الاسم',
    accountType: 'نوع الحساب',
    detailType: 'النوع التفصيلي',
    balanceIn: 'الرصيد ({{currency}})',
    balance: 'الرصيد',
    action: 'الإجراء',
    makeInactive: 'إلغاء التنشيط',
    makeActive: 'تنشيط',
    setOpeningBalance: 'تحديد الرصيد الافتتاحي',
    systemAccount: 'حساب نظام',
    controlAccountTip: 'حساب مراقبة: تُرحَّل إليه المستندات وليس قيود اليومية.',
    systemAccountTip: 'حساب نظام: لا يمكن إلغاء تنشيطه.',
    moreActions: 'إجراءات أخرى لحساب {{name}}',
    activeAgain: 'أصبح {{name}} نشطاً مرة أخرى.',
    nowInactive: 'أصبح {{name}} غير نشط.',
    inactiveTitle: 'إلغاء تنشيط {{name}}؟',
    inactiveBody:
      'تُخفى الحسابات غير النشطة من القوائم ولا يمكن استخدامها في معاملات جديدة، ويبقى سجلها في التقارير. يجب تصفية رصيد الحساب أولاً.',
  },
  accountDrawer: {
    newTitle: 'حساب جديد',
    editTitle: 'تعديل الحساب',
    newDescription: 'أضف حساباً إلى الدليل.',
    accountType: 'نوع الحساب',
    detailType: 'النوع التفصيلي',
    lockedType: 'لا يمكن تغييره بعد وجود معاملات على الحساب.',
    systemAccount: 'حساب نظام.',
    name: 'الاسم',
    number: 'الرقم',
    description: 'الوصف',
    subAccount: 'جعله حساباً فرعياً',
    parent: 'الحساب الرئيسي',
    chooseAccount: 'اختر حساباً',
    currency: 'العملة',
    currencyHint: 'عملة الحساب البنكي أو البطاقة؛ كشوف حسابه بها.',
    homeCurrency: '{{code}} – العملة الأساسية',
    defaultTaxCode: 'رمز {{tax}} الافتراضي',
    saved: 'تم حفظ الحساب {{code}} {{name}}.',
    enterName: 'أدخل اسماً.',
    enterNumber: 'أدخل رقماً (حتى 20 حرفاً أو رقماً أو نقطة أو شرطة).',
    controlNote: 'لا يمكن لقيود اليومية اليدوية الترحيل إلى هذا الحساب.',
    chooseParent: 'اختر الحساب الرئيسي.',
  },
  suggest: {
    title: 'اقتراح الأسماء العربية',
    description:
      'الحسابات التي ليس لها اسم عربي، مع اقتراح من قالب الدليل أو المسرد. راجع كل اسم قبل التطبيق، وألغِ تحديد ما تريد إبقاءه بالإنجليزية.',
    allHave: 'لجميع الحسابات أسماء عربية.',
    account: 'الحساب',
    arabicName: 'الاسم بالعربية',
    source: 'المصدر',
    sourceTemplate: 'قالب الدليل',
    sourceGlossary: 'المسرد',
    sourceArabicName: 'الاسم عربي',
    sourceNone: 'لا يوجد',
    needsEnglish: 'الاسم عربي: أضف للحساب اسماً إنجليزياً أيضاً.',
    applyThis: 'تطبيق الاسم العربي لحساب {{name}}',
    arabicNameOf: 'الاسم العربي لحساب {{name}}',
    apply_zero: 'تطبيق',
    apply_one: 'تطبيق اسم واحد',
    apply_two: 'تطبيق اسمين',
    apply_few: 'تطبيق {{count}} أسماء',
    apply_many: 'تطبيق {{count}} اسماً',
    apply_other: 'تطبيق {{count}} اسم',
    added_zero: 'لم يُضف أي اسم عربي.',
    added_one: 'أُضيف اسم عربي واحد.',
    added_two: 'أُضيف اسمان عربيان.',
    added_few: 'أُضيفت {{count}} أسماء عربية.',
    added_many: 'أُضيف {{count}} اسماً عربياً.',
    added_other: 'أُضيف {{count}} اسم عربي.',
    kept_zero: '',
    kept_one: 'وكان لحساب واحد اسم عربي فأُبقي عليه.',
    kept_two: 'وكان لحسابين اسمان عربيان فأُبقي عليهما.',
    kept_few: 'وكان لـ {{count}} حسابات أسماء عربية فأُبقي عليها.',
    kept_many: 'وكان لـ {{count}} حساباً أسماء عربية فأُبقي عليها.',
    kept_other: 'وكان لـ {{count}} حساب أسماء عربية فأُبقي عليها.',
  },
  home: {
    title: 'الرئيسية',
    description: 'أعمالك خلال الفترة المحددة. تظهر البنود التي تحتاج إلى انتباهك أولاً.',
    period: 'الفترة',
    leaveBusinessView: 'مغادرة عرض الأعمال',
    upload: 'رفع فواتير الموردين والإيصالات والفواتير',
    loadError: 'تعذّر تحميل الملخص',
    selectClientHint: 'استخدم مبدّل الشركات في الشريط العلوي.',
    inTheBank: 'في البنك',
    bankAccounts_zero: 'لا توجد حسابات',
    bankAccounts_one: 'حساب واحد',
    bankAccounts_two: 'حسابان',
    bankAccounts_few: '{{count}} حسابات',
    bankAccounts_many: '{{count}} حساباً',
    bankAccounts_other: '{{count}} حساب',
    owedToYou: 'مستحق لك',
    youOwe: 'مستحق عليك',
    overdueOpen: '{{overdue}} متأخرة · {{open}} مفتوحة',
    profit: 'الربح',
    changeOn: '{{change}} مقارنة بـ{{previous}}',
    moneyOwedToYou: 'المبالغ المستحقة لك',
    moneyYouOwe: 'المبالغ المستحقة عليك',
    needsAttention: 'بحاجة إلى انتباهك',
    approvals_zero: 'لا توجد مستندات بانتظار موافقتك',
    approvals_one: 'مستند بانتظار موافقتك',
    approvals_two: 'مستندان بانتظار موافقتك',
    approvals_few: 'مستندات بانتظار موافقتك',
    approvals_many: 'مستنداً بانتظار موافقتك',
    approvals_other: 'مستند بانتظار موافقتك',
    rejected_zero: 'لا توجد مسودات معادة',
    rejected_one: 'مسودة أعادها محاسبك مع ملاحظات',
    rejected_two: 'مسودتان أعادهما محاسبك مع ملاحظات',
    rejected_few: 'مسودات أعادها محاسبك مع ملاحظات',
    rejected_many: 'مسودة أعادها محاسبك مع ملاحظات',
    rejected_other: 'مسودة أعادها محاسبك مع ملاحظات',
    submitted_zero: 'لا توجد مسودات لدى محاسبك',
    submitted_one: 'مسودة لدى محاسبك للموافقة',
    submitted_two: 'مسودتان لدى محاسبك للموافقة',
    submitted_few: 'مسودات لدى محاسبك للموافقة',
    submitted_many: 'مسودة لدى محاسبك للموافقة',
    submitted_other: 'مسودة لدى محاسبك للموافقة',
    inbox_zero: 'لا توجد ملفات قيد المعالجة',
    inbox_one: 'ملف مرفوع يعالجه محاسبك',
    inbox_two: 'ملفان مرفوعان يعالجهما محاسبك',
    inbox_few: 'ملفات مرفوعة يعالجها محاسبك',
    inbox_many: 'ملفاً مرفوعاً يعالجه محاسبك',
    inbox_other: 'ملف مرفوع يعالجه محاسبك',
    allCaughtUp: 'لا توجد بنود تحتاج إلى انتباهك.',
    allCaughtUpOwner: 'ارفع فواتير الموردين والإيصالات والفواتير عند استلامها.',
    cashPosition: 'الوضع النقدي',
    card: '(بطاقة)',
    setOpeningOf: 'تحديد الرصيد الافتتاحي لحساب {{name}}',
    balancesNote: 'الأرصدة في الدفاتر كما سجّلها محاسبك.',
    selectToSetOpening: 'اختر حساباً لتحديد رصيده الافتتاحي.',
    openInvoices_zero: 'لا توجد فواتير مفتوحة',
    openInvoices_one: 'فاتورة مفتوحة واحدة',
    openInvoices_two: 'فاتورتان مفتوحتان',
    openInvoices_few: '{{count}} فواتير مفتوحة',
    openInvoices_many: '{{count}} فاتورة مفتوحة',
    openInvoices_other: '{{count}} فاتورة مفتوحة',
    openBills_zero: 'لا توجد فواتير موردين مفتوحة',
    openBills_one: 'فاتورة مورد مفتوحة واحدة',
    openBills_two: 'فاتورتا مورد مفتوحتان',
    openBills_few: '{{count}} فواتير موردين مفتوحة',
    openBills_many: '{{count}} فاتورة مورد مفتوحة',
    openBills_other: '{{count}} فاتورة مورد مفتوحة',
    overdue: 'متأخرة',
    viewInvoices: 'عرض الفواتير',
    viewBills: 'عرض فواتير الموردين',
    returnLabel: 'إقرار {{label}}',
    refundable: 'قابل للاسترداد',
    payable: 'مستحق الدفع',
    due: 'يستحق في {{date}}',
    taxReturns: 'إقرارات {{tax}}',
    noReturnDue: 'لا يوجد إقرار {{tax}} مستحق.',
    recentUploads: 'أحدث الملفات المرفوعة',
    allDocuments: 'جميع المستندات',
    nothingUploaded:
      'لم يُرفع شيء بعد. أرسل فواتير الموردين والإيصالات والفواتير إلى محاسبك من المستندات.',
    recordedAsExpense: '{{status}} كمصروف لـ {{name}}',
    recordedAsInvoice: '{{status}} كفاتورة لـ {{name}}',
    recordedAsBill: '{{status}} كفاتورة مورد لـ {{name}}',
  },
  vatStatus: {
    open: 'الفترة مفتوحة',
    due: 'مستحق',
    overdue: 'متأخر',
    filed: 'مقدَّم',
    paid: 'مدفوع',
  },
  inboxStatus: {
    received: 'مستلم',
    in_review: 'قيد المراجعة',
    recorded: 'مسجَّل',
    rejected: 'غير مسجَّل',
  },
  periods: {
    this_month: 'هذا الشهر',
    last_month: 'الشهر الماضي',
    this_quarter: 'هذا الربع',
    this_year: 'هذه السنة المالية',
    prev_this_month: 'الشهر الماضي',
    prev_last_month: 'الشهر السابق له',
    prev_this_quarter: 'الربع الماضي',
    prev_this_year: 'السنة المالية الماضية',
  },
  dashboard: {
    profitAndLoss: 'الأرباح والخسائر',
    viewReport: 'عرض التقرير',
    income: 'الإيرادات',
    expenses: 'المصروفات',
    netProfit: 'صافي الربح',
    thisPeriod: 'هذه الفترة',
    viewExpenses: 'عرض المصروفات',
    noExpenses: 'لا توجد مصروفات في هذه الفترة.',
    everythingElse: 'كل ما عدا ذلك',
    totalSpent: 'إجمالي الإنفاق',
    invoices: 'الفواتير',
    unpaid: 'غير مدفوعة',
    paid: 'مدفوعة',
    overdue: 'متأخرة',
    notDueYet: 'غير مستحقة بعد',
    notDeposited: 'غير مودعة',
    deposited30: 'مودعة (آخر 30 يوماً)',
    bankAccounts: 'الحسابات البنكية',
    goToBanking: 'الانتقال إلى البنوك',
    noBankAccounts: 'لا توجد حسابات بنكية أو بطاقات ائتمان بعد.',
    toReview: '{{count}} للمراجعة',
    bankBalance: 'رصيد البنك',
    inOutss: 'في Outss Accounting',
    setOpeningBalance: 'تحديد الرصيد الافتتاحي',
    sales: 'المبيعات',
    salesChart: 'الإيرادات الشهرية لآخر اثني عشر شهراً',
    showFigures: 'عرض الأرقام',
    hideFigures: 'إخفاء الأرقام',
  },
  businessMenu: {
    draftForApproval: '{{label}} (مسودة للموافقة)',
  },
  notifications: {
    title: 'الإشعارات',
    unreadAria: 'الإشعارات، {{count}} غير مقروءة',
    markAllRead: 'تحديد الكل كمقروء',
    none: 'لا توجد لديك إشعارات.',
    settings: 'إعدادات الإشعارات',
  },
  businessView: {
    label: 'عرض الأعمال',
    on: 'عرض الأعمال مفعّل',
    back: 'العودة إلى عرض المحاسب',
    see: 'شاهد ما يراه العميل',
    cashSummary: 'ملخص النقد',
    cashBalance: 'الرصيد النقدي',
    acrossAccounts_zero: 'لا توجد حسابات بنكية أو نقدية',
    acrossAccounts_one: 'في حساب بنكي أو نقدي واحد',
    acrossAccounts_two: 'في حسابين بنكيين أو نقديين',
    acrossAccounts_few: 'في {{count}} حسابات بنكية ونقدية',
    acrossAccounts_many: 'في {{count}} حسابًا بنكيًا ونقديًا',
    acrossAccounts_other: 'في {{count}} حساب بنكي ونقدي',
    netPosition: 'صافي المركز',
    netPositionHint:
      'الرصيد النقدي مضافًا إليه المبالغ المستحقة لك، مطروحًا منه المبالغ المستحقة عليك.',
    shortcuts: 'اختصارات',
    shortcut: {
      upload: 'رفع المستندات',
      newInvoice: 'فاتورة جديدة',
      newBill: 'فاتورة مورد جديدة',
      newExpense: 'مصروف جديد',
      receivePayment: 'استلام دفعة',
      invoices: 'الفواتير',
      bills: 'فواتير الموردين',
      reports: 'التقارير',
    },
    noOverdueInvoices: 'لا توجد فواتير متأخرة',
    noOverdueBills: 'لا توجد فواتير موردين متأخرة',
    noReturnDue: 'لا يوجد إقرار مستحق.',
    thisMonth: 'هذا الشهر · {{from}} – {{to}}',
    netLoss: 'صافي الخسارة',
    comparedWithLastMonth: '{{change}} مقارنةً بالشهر الماضي',
    viewAll: 'عرض الكل',
  },
  demo: {
    asOwner: 'أنت في النسخة التجريبية بصفتك صاحب عمل',
    asAccountant: 'أنت في النسخة التجريبية بصفتك محاسباً',
    rest: 'مع شركات نموذجية يشاركها زوار آخرون. سجّل ما تشاء؛ تُعاد تهيئة الدفاتر من حين لآخر.',
    switchToOwner: 'التبديل إلى صاحب العمل',
    switchToAccountant: 'التبديل إلى المحاسب',
    createAccount: 'أنشئ حسابك',
  },
  search: {
    label: 'بحث',
    placeholder: 'ابحث في المعاملات وجهات الاتصال والحسابات',
    results: 'نتائج البحث',
    searching: 'جارٍ البحث…',
    noResults: 'لا توجد نتائج لـ «{{q}}»',
    noRecent: 'لا توجد معاملات حديثة',
    selectClientFirst: 'اختر شركة أولاً',
    recent: 'المعاملات الحديثة',
    transactions: 'المعاملات',
    contacts: 'العملاء والموردون',
    accounts: 'الحسابات',
    items: 'المنتجات والخدمات',
  },
  files: {
    download: 'تنزيل',
    dropHere: 'اسحب الملفات وأفلتها هنا، أو',
    browse: 'استعرض',
    types: 'PDF أو صورة (PNG أو JPEG أو WebP أو HEIC)',
    upTo: '{{types}}، حتى {{mb}} ميغابايت',
    chooseFiles: 'اختر الملفات',
    fromInbox: 'من صندوق المستندات:',
    uploadedBy: '{{kind}} رفعه {{who}} في {{date}}',
    someone: 'شخص ما',
    savingKeeps: 'عند الحفظ يبقى الملف مرفقاً ويُسجَّل الملف المرفوع.',
  },
  inboxKind: {
    bill: 'فاتورة مورد',
    receipt: 'إيصال',
    invoice: 'فاتورة',
    other: 'أخرى',
  },
  decision: {
    optional: '(اختياري)',
  },
  approvals: {
    title: 'الموافقات',
    staffDescription:
      'المسودات التي قدّمها العميل لموافقتك، والمسودات التي طلبت من العميل اعتمادها.',
    clientDescription:
      'الفواتير وفواتير الموردين التي طلب محاسبك اعتمادها، والمسودات التي قدّمتها إليه.',
    waiting: 'بالانتظار',
    history: 'السجل',
    loadError: 'تعذّر تحميل الموافقات',
    nothingWaiting: 'لا يوجد شيء بانتظار الموافقة',
    noDecisions: 'لا توجد قرارات بعد',
    staffHint: 'استخدم «اطلب موافقة العميل» على مسودة فاتورة أو فاتورة مورد.',
    clientHint: 'سيطلب منك محاسبك الموافقة هنا عندما تحتاج مسودة إلى موافقتك.',
    waitingForYou: 'بانتظارك',
    nothingForYou: 'لا يوجد ما يحتاج إلى قرارك.',
    waitingClientOrPost: 'بانتظار العميل أو جاهزة للترحيل',
    waitingAccountant: 'بانتظار محاسبك',
    nothingHere: 'لا يوجد شيء هنا.',
    document: 'المستند',
    contact: 'العميل أو المورد',
    date: 'التاريخ',
    total: 'الإجمالي',
    asked: 'الطلب',
    decision: 'القرار',
    state: 'الحالة',
    action: 'الإجراء',
    invoiceNo: 'فاتورة {{number}}',
    billNo: 'فاتورة مورد {{number}}',
    draft: '(مسودة)',
    openToPost: 'فتح للترحيل',
    readyToPost: 'اعتمدها العميل، جاهزة للترحيل',
    awaitingClient: 'بانتظار العميل',
    awaitingAccountant: 'بانتظار المحاسب',
    status_pending: 'بانتظار الموافقة',
    status_approved: 'معتمدة',
    status_rejected: 'مرفوضة',
    status_cancelled: 'مسحوبة',
    docInvoice: 'الفاتورة {{number}} لـ {{name}}',
    docBill: 'فاتورة المورد {{number}} لـ {{name}}',
    approve: 'اعتماد',
    approveAndPost: 'اعتماد وترحيل',
    reject: 'رفض',
    withdraw: 'سحب',
    withdrawConfirm: 'سحب طلب الموافقة هذا؟',
    withdrawn: 'سُحب طلب الموافقة.',
    approvedPosted: 'تم الاعتماد: رُحّلت {{doc}}.',
    approvedAccountant: 'تم الاعتماد: سيرحّل محاسبك {{doc}}.',
    rejected: 'تم الرفض: أُعيدت {{doc}} مع تعليقك.',
    approveTitle: 'اعتماد {{doc}}؟',
    approveStaff: 'يؤدي الاعتماد إلى ترحيلها إلى الدفاتر بالرقم التالي. يصل تعليقك إلى العميل.',
    approveClient: 'يرحّلها فريق المحاسبة بعد موافقتك. يصل تعليقك إلى محاسبك.',
    rejectTitle: 'رفض {{doc}}؟',
    rejectDescription: 'تبقى مسودة. اذكر ما يجب تغييره؛ يُرسل التعليق مع الرفض.',
    comment: 'تعليق',
    sentToClient: 'أُرسلت إلى العميل للموافقة.',
    askedBy: 'طلبها {{who}} في {{date}}',
    submittedBy: 'قدّمها {{who}} في {{date}}',
    decidedBy: '{{status}} بواسطة {{who}} في {{date}}',
    note: 'ملاحظة: {{text}}',
    commentText: 'تعليق: {{text}}',
    changeAndResend: 'عدّل المسودة واحفظها لإرسالها للموافقة مرة أخرى.',
    wantClientInvoice: 'هل تريد أن يؤكد العميل هذه الفاتورة قبل ترحيلها؟',
    wantClientBill: 'هل تريد أن يؤكد العميل فاتورة المورد هذه قبل ترحيلها؟',
    askClient: 'اطلب موافقة العميل',
    askTitleInvoice: 'طلب موافقة العميل على هذه الفاتورة؟',
    askTitleBill: 'طلب موافقة العميل على فاتورة المورد هذه؟',
    askDescription:
      'يُبلَّغ ملاك الشركة ويرونها في صفحتهم الرئيسية. لا تُرحَّل حتى يعتمدوها (أو تسحب الطلب).',
    sendForApproval: 'إرسال للموافقة',
    noteToClient: 'ملاحظة للعميل',
  },
  attachments: {
    title: 'المرفقات',
    attached: 'تم إرفاق {{name}}.',
    filesAttached_zero: 'لم يُرفق أي ملف.',
    filesAttached_one: 'تم إرفاق ملف واحد.',
    filesAttached_two: 'تم إرفاق ملفين.',
    filesAttached_few: 'تم إرفاق {{count}} ملفات.',
    filesAttached_many: 'تم إرفاق {{count}} ملفاً.',
    filesAttached_other: 'تم إرفاق {{count}} ملف.',
    removed: 'تمت إزالة {{name}}.',
    remove: 'إزالة {{name}}',
    removeConfirm: 'إزالة {{name}} من هذا السجل؟',
    uploading: 'جارٍ الرفع…',
    dropToAttach: 'أفلت الملفات لإرفاقها، أو',
  },
  messages: {
    title: 'الرسائل',
    staffDescription: 'محادثتك مع {{client}}. يراها الجميع لدى العميل ولدى Outss.',
    thisClient: 'هذا العميل',
    clientDescription: 'محادثتك مع محاسبك في Outss. يراها الجميع في شركتك ولدى Outss.',
    loadError: 'تعذّر تحميل الرسائل.',
    none: 'لا توجد رسائل بعد',
    staffEmpty: 'اكتب إلى العميل أدناه؛ يُبلَّغ ملاك الشركة في التطبيق وبالبريد الإلكتروني.',
    clientEmpty: 'اسأل محاسبك عن أي شيء أدناه؛ يُبلَّغ فوراً.',
    loadEarlier: 'تحميل الرسائل السابقة',
    you: 'أنت',
    client: 'العميل',
    message: 'الرسالة',
    placeholder: 'اكتب رسالة',
    sendHint: 'Ctrl+Enter للإرسال',
    sending: 'جارٍ الإرسال…',
    send: 'إرسال',
    tooLong: '4,000 حرف كحد أقصى',
  },
  kindHint: {
    bill: 'للدفع',
    receipt: 'مدفوع مسبقاً',
    invoice: 'صادرة لعميل',
    other: 'أي شيء آخر',
  },
  inbox: {
    title: 'المستندات',
    staffDescription:
      'فواتير الموردين والإيصالات والفواتير التي رفعها العميل والفريق. حوّل كلاً منها إلى مسودة فاتورة مورد أو مصروف، أو عالجها بالمحاسب الذكي؛ يبقى الملف مرفقاً.',
    clientDescription:
      'ارفع فواتير الموردين والإيصالات والفواتير لمحاسبك. سترى هنا متى سُجّل كل منها.',
    chip_open: 'للمراجعة',
    chip_recorded: 'مسجَّلة',
    chip_rejected: 'غير مسجَّلة',
    chip_all: 'الكل',
    today: 'اليوم',
    yesterday: 'أمس',
    lastWeek: 'آخر 7 أيام',
    older: 'أقدم',
    sentToAi: 'أُرسل {{name}} إلى المحاسب الذكي.',
    open: 'فتح',
    uploadedOne: 'تم رفع {{name}}، وأُبلغ محاسبك.',
    uploadedMany_zero: 'لم يُرفع أي ملف.',
    uploadedMany_one: 'تم رفع ملف واحد، وأُبلغ محاسبك.',
    uploadedMany_two: 'تم رفع ملفين، وأُبلغ محاسبك.',
    uploadedMany_few: 'تم رفع {{count}} ملفات، وأُبلغ محاسبك.',
    uploadedMany_many: 'تم رفع {{count}} ملفاً، وأُبلغ محاسبك.',
    uploadedMany_other: 'تم رفع {{count}} ملف، وأُبلغ محاسبك.',
    statusChanged: '{{name}}: {{status}}.',
    upload: 'رفع',
    whatIsIt: 'ما نوعه؟',
    documentType: 'نوع المستند',
    noteForAccountant: 'ملاحظة لمحاسبك',
    note: 'ملاحظة',
    notePlaceholder: 'مثلاً: دُفع بالبطاقة، يُقسم مع فرع دبي',
    uploading: 'جارٍ الرفع…',
    dropHere: 'اسحب فواتير الموردين والإيصالات والفواتير وأفلتها هنا، أو',
    status: 'الحالة',
    searchLabel: 'البحث في المستندات',
    searchPlaceholder: 'ابحث عن ملف أو ملاحظة أو شخص',
    loadError: 'تعذّر تحميل المستندات',
    noMatch: 'لا توجد مستندات مطابقة لبحثك',
    nothingToReview: 'لا يوجد ما يُراجع',
    noDocuments: 'لا توجد مستندات هنا',
    tryDifferent: 'جرّب اسم ملف أو ملاحظة أو شخصاً آخر.',
    uploadedAppear: 'تظهر هنا فواتير الموردين والإيصالات والفواتير المرفوعة.',
    ownersUpload: 'يرفع ملاك الشركة فواتير الموردين والإيصالات والفواتير هنا.',
    rejectTitle: 'تحديد {{name}} كغير قابل للتسجيل؟',
    thisUpload: 'هذا الملف',
    rejectDescription: 'يُبلَّغ من رفعه بالسبب. يمكنك إعادته للمراجعة لاحقاً.',
    reject: 'رفض',
    reason: 'السبب',
    by: 'بواسطة {{name}}',
    notRecorded: 'غير مسجَّل: {{reason}}',
    docBill: 'فاتورة مورد {{number}}',
    docExpense: 'مصروف {{number}}',
    docInvoice: 'فاتورة {{number}}',
    draft: '(مسودة)',
    inAi: 'في المحاسب الذكي',
    openInAi: 'فتح في المحاسب الذكي',
    processWithAi: 'المعالجة بالمحاسب الذكي',
    createBill: 'إنشاء فاتورة مورد',
    createExpense: 'إنشاء مصروف',
    moreActions: 'إجراءات أخرى',
    markInReview: 'تحديد كقيد المراجعة',
    backToReceived: 'إعادة إلى المستلمة',
    cannotRecord: 'لا يمكن تسجيله…',
  },
  mobile: {
    home: 'الرئيسية',
    reports: 'التقارير',
    chat: 'المحادثة',
    yourAccountant: 'محاسبك',
    askAi: 'اسأل الذكاء',
    askOutssAi: 'اسأل Outss الذكي',
    more: 'المزيد',
    chooseCompany: 'اختر شركة',
    invoice: 'فاتورة',
    bill: 'فاتورة مورد',
    new: 'جديد',
    upload: 'رفع فواتير الموردين والإيصالات والفواتير',
    report: 'التقرير',
    taxes: 'الضرائب',
    notifications: 'الإشعارات',
    documents: 'المستندات',
    sales: 'المبيعات',
    expenses: 'المصروفات',
    switchCompany: 'تبديل الشركة',
    switch: 'تبديل',
    demo: 'تجريبي',
    india: 'الهند',
    uae: 'الإمارات',
    yourBusiness: 'أعمالك',
    documentsSubtitle: 'الملفات المرفوعة والموافقات',
    salesSubtitle: 'الفواتير والعملاء',
    expensesSubtitle: 'فواتير الموردين والمصروفات والموردون',
    yourCompany: 'شركتك',
    customerStatements: 'كشوف حسابات العملاء',
    companySettings: 'إعدادات الشركة',
    language: 'اللغة',
    signOut: 'تسجيل الخروج',
    version: 'Outss Accounting · الإصدار {{version}}',
    restartTitle: 'أعد تشغيل التطبيق',
    restartBody: 'أغلق Outss Accounting وافتحه مجدداً لعرضه من اليمين إلى اليسار.',
    restartBodyLtr: 'أغلق Outss Accounting وافتحه مجدداً لعرضه من اليسار إلى اليمين.',
    ok: 'حسناً',
  },
  ui: {
    addNew: 'إضافة جديد',
    openCalendar: 'فتح التقويم',
    datePlaceholder: 'يوم/شهر/سنة',
    close: 'إغلاق',
    dismiss: 'إخفاء',
    newVersion: 'يتوفر إصدار جديد من Outss Accounting.',
    reload: 'إعادة التحميل',
    comingSoon: 'قريباً',
    pageNotFound: 'الصفحة غير موجودة',
    notAvailable: 'هذه الميزة غير متاحة بعد.',
    noPage: 'لا توجد صفحة على {{path}}.',
    goToDashboard: 'الانتقال إلى لوحة المعلومات',
    noMatches: 'لا توجد نتائج مطابقة',
  },
  profile: {
    language: 'اللغة',
    languageHint: 'اللغة التي يستخدمها التطبيق معك. يختار كل شخص لغته.',
    languageSaved: 'تم حفظ اللغة.',
  },
};
